import { ApolloQueryResult } from 'apollo-client';
import { sortBy } from 'lodash';

import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import {
    ProductsListQuery,
    ProductsListQuery_products_rowset_restrictions,
    ProductsListQuery_products_rowset_restrictions_conditions
} from '../../typings/graphql-grid';
import {
    ConditionsCorrectionsApiKeys,
    ConditionsCorrectionsKeys,
    ConditionsKeys,
    ConditionsSocialDemoKeys,
    CpmGroupShowConditionType,
    ProductsList,
    ProductsListItemRestriction,
    ProductsListItemRestrictionCondition
} from '../../typings/groupsEditState';
import { TargetPlatformTags } from '../../typings/targetTagsEditor';
import { mapRestrictionGroupRowTypeToGroupType } from '../common/common';

const correctionApiToCorrectionKeys = {
    [ConditionsCorrectionsApiKeys.correction_device]: ConditionsCorrectionsKeys.device,
    [ConditionsCorrectionsApiKeys.correction_socdem]: ConditionsCorrectionsKeys.socdem,
    [ConditionsCorrectionsApiKeys.correction_weather]: ConditionsCorrectionsKeys.weather,
    [ConditionsCorrectionsApiKeys.correction_target_audience]: ConditionsCorrectionsKeys.target_audience,
    [ConditionsCorrectionsApiKeys.correction_traffic]: ConditionsCorrectionsKeys.traffic
};

export const mapProductsListFromApi = (result: ApolloQueryResult<ProductsListQuery>): ProductsList => {
    const { products } = result.data;
    const cpmGroupTypesOrder = GroupsHelpers.getCpmGroupTypesOrderForGroupCreation();

    return products.rowset.reduce((productsList, product): ProductsList => {
        const type = product.type;
        const restrictions = mapProductsListItemRestrictionFromApi(product.restrictions);

        productsList[type] = {
            id: String(product.id),
            type: type,
            restrictions: sortBy(restrictions, restriction => cpmGroupTypesOrder.indexOf(restriction.groupType))
        };

        return productsList;
    }, {});
};

export const mapProductsListItemRestrictionConditionsFromApi = (
    conditionsData: ProductsListQuery_products_rowset_restrictions_conditions[]
): ProductsListItemRestrictionCondition[] => {
    const conditions: ProductsListItemRestrictionCondition[] = [];
    let correctionIndex: number;
    let socialDemoIndex: number;

    conditionsData.forEach(conditionData => {
        if (Object.keys(correctionApiToCorrectionKeys).includes(conditionData.name)) {
            if (correctionIndex >= 0) {
                conditions[correctionIndex].nestedConditions = [
                    correctionApiToCorrectionKeys[conditionData.name]
                ].concat(conditions[correctionIndex].nestedConditions);
            } else {
                correctionIndex = conditions.length;
                conditions.push({
                    name: ConditionsKeys.correction,
                    nestedConditions: [correctionApiToCorrectionKeys[conditionData.name]]
                });
            }
        } else if (Object.keys(ConditionsSocialDemoKeys).includes(conditionData.name)) {
            if (socialDemoIndex >= 0) {
                conditions[socialDemoIndex].nestedConditions = [ConditionsSocialDemoKeys[conditionData.name]].concat(
                    conditions[socialDemoIndex].nestedConditions
                );
            } else {
                socialDemoIndex = conditions.length;
                conditions.push({
                    name: ConditionsKeys.social_demo,
                    nestedConditions: [ConditionsSocialDemoKeys[conditionData.name]]
                });
            }
        } else if (conditionData.name === ConditionsKeys.bs_target_tags) {
            // В conditionData.values[0] для bs_target_tags приходит объект с ключами – разрешёнными для выбора ПИ-тегами площадок показа
            const targetPlatforms = Object.keys(conditionData.values[0] || {});

            conditions.push({
                name: ConditionsKeys.bs_target_tags,
                // Приведение нужно, потому что c бэка приходит GraphQlMap_String_ObjectScalar
                nestedConditions: targetPlatforms as TargetPlatformTags[]
            });
        } else {
            conditions.push({
                name: ConditionsKeys[conditionData.name]
            });
        }
    });

    return conditions;
};

export const mapProductsListItemRestrictionFromApi = (
    restrictions: ProductsListQuery_products_rowset_restrictions[]
): Array<ProductsListItemRestriction> => {
    return restrictions.map(restriction => {
        const conditions = mapProductsListItemRestrictionConditionsFromApi(restriction.conditions);
        const isConditionsWithPhrases = conditions.some(condition => condition.name === ConditionsKeys.phrases);

        return {
            id: String(restriction.id),
            groupType: mapRestrictionGroupRowTypeToGroupType(restriction.groupType),
            showConditionType: isConditionsWithPhrases
                ? CpmGroupShowConditionType.KEYWORD
                : CpmGroupShowConditionType.USER_PROFILE,
            conditions,
            active: Boolean(restriction.active)
        };
    });
};

export const getRestrictionByShowConditionType = (
    showConditionType: CpmGroupShowConditionType,
    restrictions: ProductsListItemRestriction[]
): ProductsListItemRestriction | undefined => {
    const conditionName = GroupsHelpers.getConditionsKeyFromShowConditionType(showConditionType);

    return restrictions.find(restriction => {
        return restriction.conditions.some(condition => condition.name === conditionName);
    });
};
